import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import * as Yup from 'yup';
import { CONFIGURATOR_QUOTATION_DISCLAIMER, CONFIGURATOR_URL_PARAM_NAME, SALESFORCE_DEALER_KEY, } from '@yme/common/constants';
import { getClearStateValue } from '@yme/components/CheckoutForm/ShippingDetailsForm/AddressBlock/helpers';
import { getAttributeByName, getQueryParamByName } from '@yme/utils/common';
import { generateConfiguratorLink } from '@yme/utils/configurator';
import { getMasterVariant, getVariationsVariants } from '@yme/utils/product';
import { FormType, GENERIC_NOTES, allowedDuplicateFields } from './constants';
export const getConfiguratorFormMetadata = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const modelName = urlParams.get('modelName');
    const configuratorUrl = urlParams.get(CONFIGURATOR_URL_PARAM_NAME) ?? '';
    const slug = urlParams.get('slug');
    const isConfiguratorLeadForm = Boolean(modelName && configuratorUrl);
    const hashParams = window.location.hash;
    const configuratorLink = generateConfiguratorLink(configuratorUrl, hashParams);
    const decodedHashString = decodeURIComponent(hashParams.slice(1));
    let parsedHashObject = null;
    try {
        parsedHashObject = JSON.parse(decodedHashString);
    }
    catch (e) { }
    const defaultSku = parsedHashObject?.sku ?? '';
    const variantSku = parsedHashObject?.variantSku ?? '';
    const sku = variantSku || defaultSku || '';
    return {
        isConfiguratorLeadForm,
        configuratorUrl,
        configuratorLink,
        modelName,
        slug,
        sku,
    };
};
export const getArrayValuesInUpperCase = (arr) => {
    return arr.map((item) => item.toUpperCase().trim());
};
export const filterDealersByServiceId = (dealers, services) => {
    return services.length
        ? dealers.filter((dealer) => dealer.serviceIds.some((id) => {
            return services.find((service) => service === id);
        }))
        : dealers;
};
export const filterDealersByInputParams = ({ dealers, productServiceCodes, dealerIdList, isShowSfDealersOnly, dealersServices, }) => {
    const filteredDealersByDealerIdList = dealerIdList.length
        ? dealers.filter((dealer) => {
            return dealerIdList.includes(dealer.vicinityId);
        })
        : dealers;
    const response = productServiceCodes.length
        ? filteredDealersByDealerIdList.filter((dealer) => {
            return dealer.serviceIds.some((serviceId) => {
                return productServiceCodes.includes(serviceId);
            });
        })
        : filteredDealersByDealerIdList;
    if (!isShowSfDealersOnly) {
        return filterDealersByServiceId(response, getArrayValuesInUpperCase(dealersServices));
    }
    return filter(filterDealersByServiceId(response, getArrayValuesInUpperCase(dealersServices)), SALESFORCE_DEALER_KEY);
};
export const sortDealersByDistance = (dealers) => {
    return dealers.sort((dealerA, dealerB) => Number(dealerA?.distance) - Number(dealerB?.distance));
};
const getConfiguratorData = () => {
    try {
        const result = JSON.parse(decodeURIComponent(window.location.hash.slice(1)));
        if (typeof result === 'object' && result !== null) {
            return result;
        }
        else {
            return null;
        }
    }
    catch (e) {
        return null;
    }
};
export const getConfiguratorParams = () => {
    return {
        configuratorData: getConfiguratorData(),
        configuratorUrl: new URLSearchParams(window.location.search).get(CONFIGURATOR_URL_PARAM_NAME),
    };
};
function transformDynamicKeys(obj, substrings, labelsFromLeadFormFields) {
    return Object.entries(obj).reduce((result, [key, value]) => {
        const matchingSubstring = substrings.find((substring) => key.includes(substring));
        const label = labelsFromLeadFormFields?.find((field) => {
            const parts = key.split('_');
            return parts[0] === field.name;
        })?.label || '';
        if (matchingSubstring && value) {
            const formattedValue = `${label}:${value}`;
            result[matchingSubstring] = result[matchingSubstring]
                ? `${result[matchingSubstring]},${formattedValue}`
                : formattedValue;
        }
        else {
            result[key] = value;
        }
        return result;
    }, {});
}
const addField = (transformValues, field) => {
    const fieldValue = get(transformValues, field);
    return fieldValue
        ? {
            [field]: fieldValue,
        }
        : {};
};
export const preparePayloadData = (data) => {
    const { formType } = data;
    if (formType === FormType.LEAD_FORM) {
        const configuratorUrl = getQueryParamByName(window.location.search, CONFIGURATOR_URL_PARAM_NAME);
        const hashParams = window.location.hash;
        const transformValues = transformDynamicKeys(data.values, allowedDuplicateFields, Object.values(data.dynamicFieldsFromLeadForm || []));
        const dynamicFieldNames = new Set(Object.values(data.dynamicFieldsFromLeadForm || {}).map((x) => x.name));
        const dynamicFields = Array.from(dynamicFieldNames).reduce((acc, name) => ({
            ...acc,
            ...addField(transformValues, name),
        }), {});
        const leadPayload = {
            firstName: get(data.values, 'firstName'),
            lastName: get(data.values, 'lastName'),
            email: get(data.values, 'email'),
            phone: get(data.values, 'phoneNumber'),
            postalCode: get(data?.addressFieldsValue, 'postalCode') || '',
            city: get(data?.addressFieldsValue, 'city') || '',
            street: get(data?.addressFieldsValue, 'address') || '',
            state: getClearStateValue(get(data?.addressFieldsValue, 'state') || ''),
            dealerAccount: get(data, 'selectedDealerId'),
            dealerAssignedDate: get(data, 'dealerAssignedDate'),
            description: get(data.values, 'description'),
            language: get(data, 'language'),
            ...dynamicFields,
        };
        if (!isEmpty(configuratorUrl)) {
            leadPayload.configuratorLink = generateConfiguratorLink(configuratorUrl, hashParams);
        }
        return {
            lead: leadPayload,
            isDescriptionRequired: get(data, 'isDescriptionRequired'),
            isAddressRequired: get(data, 'isAddressRequired'),
            isDealerDataRequired: get(data, 'isDealerDataRequired'),
            isPhoneRequired: get(data, 'isPhoneRequired'),
            isStateEnabled: get(data, 'isStateEnabled'),
            campaignId: get(data, 'campaignId'),
            consents: get(data, 'consents'),
            ...(Boolean(data.selectedSimulation)
                ? {
                    selectedSimulation: data.selectedSimulation,
                }
                : {}),
        };
    }
    return {
        customer: {
            firstName: get(data.values, 'firstName'),
            lastName: get(data.values, 'lastName'),
            email: get(data.values, 'email'),
            phone: get(data.values, 'phoneNumber'),
            language: get(data, 'language'),
            userName: get(data, 'userName') || '',
        },
        followUp: get(data.values, 'followUp'),
        requestedDateTime: data?.requestDate,
        description: get(data.values, 'myVehicle'),
        dealerId: get(data, 'selectedDealerId'),
        consents: get(data, 'consents'),
        vehicle: {
            modelName: get(data.values, 'modelName'),
            licensePlate: get(data.values, 'licensePlateNumber'),
            mileage: get(data.values, 'vehicleKM'),
        },
        category: get(data.values, 'maintenanceCategory'),
    };
};
export const getDynamicInitialValues = (leadFormFields) => leadFormFields?.reduce((acc, field, index) => {
    const baseKey = field.name === GENERIC_NOTES ? `${GENERIC_NOTES}_${index}` : field.name;
    return {
        ...acc,
        [baseKey]: '',
    };
}, {});
export const getDynamicValidationFields = (leadFormFields, requiredFieldError) => leadFormFields?.reduce((acc, field, index) => {
    const validatedRule = field.required && requiredFieldError
        ? Yup.string().required(requiredFieldError)
        : Yup.string();
    const baseKey = field.name === GENERIC_NOTES ? `${GENERIC_NOTES}_${index}` : field.name;
    return {
        ...acc,
        [baseKey]: validatedRule,
    };
}, {});
export const getDataFromLeadFormFields = (leadFromFields) => leadFromFields?.reduce((acc, field, index) => {
    const baseKey = field.name === GENERIC_NOTES ? `${GENERIC_NOTES}_${index}` : field.name;
    return {
        ...acc,
        [baseKey]: {
            label: field.label,
            name: field.name,
        },
    };
}, []);
export const getConfiguratorProductDisclaimer = ({ configuratorProduct, sku, }) => {
    const masterVariant = getMasterVariant(configuratorProduct.variants);
    const allVariants = getVariationsVariants(configuratorProduct);
    const currentVariant = allVariants.find((variant) => variant.sku === sku);
    const variant = currentVariant ?? masterVariant;
    return variant ? getAttributeByName(variant?.attributes, CONFIGURATOR_QUOTATION_DISCLAIMER) : '';
};
